import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REQUEST_TYPES = [
  { id: 'access', label: 'Access — copy of my data (Article 15)' },
  { id: 'rectification', label: 'Rectification — correct something (Article 16)' },
  { id: 'erasure', label: 'Erasure — delete my data (Article 17)' },
  { id: 'restriction', label: 'Restriction — limit how my data is used (Article 18)' },
  { id: 'portability', label: 'Portability — machine-readable export (Article 20)' },
  { id: 'objection', label: 'Object to processing (Article 21)' },
  { id: 'withdraw_consent', label: 'Withdraw consent' },
  { id: 'other', label: 'Other / general question' },
];

const DataRequestPage = () => {
  const [email, setEmail] = useState('');
  const [requestType, setRequestType] = useState('access');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setResult(null); setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/dsar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          request_type: requestType,
          details: details.trim() || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setResult(body); setEmail(''); setDetails('');
      } else if (res.status === 429) {
        setError('Too many requests from your network. Please wait an hour and try again.');
      } else {
        setError(body.detail || 'Could not submit your request. Please try again.');
      }
    } catch { setError('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f0]" data-testid="data-request-page">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center text-red-600 hover:text-red-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
        </Link>

        <div className="flex items-start gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">Your data, your rights</h1>
            <p className="text-gray-600">
              Use this form to exercise any UK GDPR right. We respond within 30 days.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm mb-6">
          <p className="text-sm text-gray-700">
            <strong>Have an account?</strong> You can exercise most rights instantly at{' '}
            <Link to="/dashboard/settings" className="text-red-600 hover:underline">
              Settings → Privacy &amp; data
            </Link>: one-click data download, and account deletion with a 30-day grace period.
          </p>
        </div>

        {result ? (
          <div data-testid="dsar-success" className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-xl font-semibold text-emerald-900 mb-1">Request received</h2>
                <p className="text-emerald-800 text-sm mb-3">
                  We&apos;ve sent a confirmation to your email. Reference:{' '}
                  <code className="bg-emerald-100 px-1.5 py-0.5 rounded text-emerald-900">{result.reference}</code>
                </p>
                <p className="text-emerald-800 text-sm">We will respond within 30 days as required by UK GDPR.</p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-5" data-testid="dsar-form">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Your email address</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" data-testid="dsar-email-input"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none" />
              <p className="text-xs text-gray-500 mt-1">
                We&apos;ll send a confirmation here and use it to verify your request.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">What right do you want to exercise?</label>
              <select value={requestType} onChange={(e) => setRequestType(e.target.value)} data-testid="dsar-type-select"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none bg-white">
                {REQUEST_TYPES.map((t) => (<option key={t.id} value={t.id}>{t.label}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Tell us more (optional)</label>
              <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={4} maxLength={2000}
                placeholder="Any details that will help us process your request faster."
                data-testid="dsar-details-input"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none" />
              <p className="text-xs text-gray-500 mt-1">{details.length}/2000</p>
            </div>
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200" data-testid="dsar-error">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <button type="submit" disabled={submitting || !email} data-testid="dsar-submit-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Submit request
            </button>
            <p className="text-xs text-gray-500 pt-2 border-t border-gray-100">
              For complaints, you can also contact the ICO at{' '}
              <a href="https://ico.org.uk" target="_blank" rel="noreferrer noopener" className="text-red-600 hover:underline">ico.org.uk</a>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default DataRequestPage;
